import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Receipt, Loader2, Plus } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { insApi, errMsg, sar } from "../../lib/insApi";

const ST = { paid: ["مدفوعة", "bg-emerald-50 text-emerald-700"], partial: ["جزئي", "bg-amber-50 text-amber-700"], unpaid: ["غير مدفوعة", "bg-rose-50 text-rose-700"] };
const lastMonth = () => { const d = new Date(); d.setDate(0); return d.toISOString().slice(0, 7); };

const PayBox = ({ inv, onDone }) => {
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const pay = async () => {
    try { await insApi.adminPayment(inv.id, { amount: Number(amount), note }); toast.success("تم تسجيل الدفعة"); setAmount(""); setNote(""); onDone(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="flex flex-wrap items-center gap-2">
      <Input data-testid={`pc-inv-amount-${inv.id}`} type="number" className="h-8 w-28" placeholder="المبلغ" value={amount} onChange={(e) => setAmount(e.target.value)} />
      <Input className="h-8 w-40" placeholder="ملاحظة / رقم التحويل" value={note} onChange={(e) => setNote(e.target.value)} />
      <Button size="sm" variant="outline" disabled={!Number(amount)} onClick={pay} data-testid={`pc-inv-pay-${inv.id}`}>تسجيل دفعة</Button>
      <button className="text-xs text-emerald-700 underline" onClick={() => setAmount(String(Math.round((inv.total - inv.paid) * 100) / 100))}>كامل المتبقي</button>
    </div>
  );
};

export const PcInsInvoices = ({ onChange }) => {
  const [items, setItems] = useState([]);
  const [period, setPeriod] = useState(lastMonth());
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => insApi.adminInvoices().then((r) => setItems(r.items)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const gen = async () => {
    setBusy(true);
    try { const r = await insApi.adminGenInvoices(period); toast.success(r.created ? `صدرت ${r.created} فاتورة` : "لا توجد وثائق جديدة غير مفوترة لهذا الشهر"); load(); onChange?.(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const done = () => { load(); onChange?.(); };
  const due = items.reduce((a, i) => a + (i.total - i.paid), 0);
  return (
    <div className="space-y-3 rounded-2xl bg-white p-4" data-testid="pc-ins-invoices">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="flex items-center gap-1.5 font-heading font-bold"><Receipt className="h-5 w-5 text-emerald-700" /> فواتير العمولة الشهرية</h3>
        <span className="text-xs text-rose-600" data-testid="pc-ins-due">المتبقي غير المحصّل: {sar(due)}</span>
        <div className="mr-auto flex items-center gap-2">
          <Input data-testid="pc-inv-period" type="month" className="h-9 w-40" value={period} onChange={(e) => setPeriod(e.target.value)} />
          <Button size="sm" disabled={busy || !period} onClick={gen} data-testid="pc-inv-generate" className="gap-1 bg-emerald-700 hover:bg-emerald-800">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} إصدار فواتير الشهر</Button>
        </div>
      </div>
      <p className="text-xs text-slate-500">تصدر الفواتير تلقائياً يوم 1 من كل شهر عن الشهر السابق، وتوصل للمزود بالبريد وتظهر في لوحته.</p>
      {items.length === 0 ? <p className="py-4 text-center text-sm text-slate-400">لا توجد فواتير بعد</p> : items.map((i) => (
        <div key={i.id} className="space-y-2 rounded-xl border border-slate-200 p-3" data-testid={`pc-inv-${i.id}`}>
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <b className="font-mono-nums" dir="ltr">{i.number}</b><span>{i.provider_name}</span><span className="text-xs text-slate-400">{i.period} · {i.items.length} وثيقة</span>
            <span className={`rounded-full px-2 py-0.5 text-xs ${ST[i.status][1]}`}>{ST[i.status][0]}</span>
            <span className="mr-auto" dir="ltr">{sar(i.paid)} / <b>{sar(i.total)}</b></span>
          </div>
          {i.payments?.length > 0 && <p className="text-xs text-slate-500">الدفعات: {i.payments.map((p) => `${sar(p.amount)} (${p.date}${p.note ? ` — ${p.note}` : ""})`).join("، ")}</p>}
          {i.status !== "paid" && <PayBox inv={i} onDone={done} />}
        </div>
      ))}
    </div>
  );
};
